import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Preloader from "../../components/custom/preLoader";
import axiosInstance, {
  getUserDetails,
  mergeUserDetails,
  isIndependentScientistUser,
  isCompanyManagerUser,
  clearAuthData,
} from "../../components/content/axiosConfig";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { describeUserRole } from "../../utils/userRole";
import { isActingAsScopedAccount, getScopedAccount } from "../../utils/accountContext";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

function initialsFromName(name) {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDisplayDateLong(isoOrYmd) {
  if (!isoOrYmd) return null;
  const d = new Date(isoOrYmd);
  if (Number.isNaN(d.getTime())) return isoOrYmd;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function truthyFlag(v) {
  return v === true || v === 1 || v === "1";
}

function computePeriodAmount(pkg, licensedPondCount) {
  if (!pkg) return null;
  const included = pkg.IncludedPonds ?? 1;
  const extra = parseFloat(pkg.ExtraPondAmount) || 0;
  const count = licensedPondCount ?? included;
  const base = parseFloat(pkg.Amount) || 0;
  return Math.round((base + Math.max(0, count - included) * extra) * 100) / 100;
}

function formatBillingPeriodLabel(amount, durationMonths) {
  const duration = parseInt(durationMonths, 10) || 12;
  const price = `$${Number(amount).toFixed(2)}`;
  if (duration === 12) return `${price} per year`;
  if (duration === 1) return `${price} per month`;
  return `${price} every ${duration} months`;
}

function formatStripeRecurringLabel(billing) {
  if (!billing || billing.amount == null) return null;
  const price = `$${Number(billing.amount).toFixed(2)}`;
  const count = billing.intervalCount || 1;
  if (billing.interval === "year") {
    return count === 1 ? `${price} per year` : `${price} every ${count} years`;
  }
  if (billing.interval === "month") {
    if (count === 12) return `${price} per year`;
    return count === 1 ? `${price} per month` : `${price} every ${count} months`;
  }
  return `${price} per ${billing.interval || "period"}`;
}

const Profile = () => {
  const userDetail = getUserDetails();
  const fisherID = userDetail.FisherID;
  const isCompanyManager = isCompanyManagerUser(userDetail);
  const viewingScopedClient = isCompanyManager && isActingAsScopedAccount();
  const [staffFlags, setStaffFlags] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [editingCompany, setEditingCompany] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    Name: "",
    Email: "",
    Phone: "",
    Address: "",
    City: "",
    State: "",
    PostalCode: "",
    Country: "",
    EIN: "",
  });

  const [fisher, setFisher] = useState({
    FullName: "",
    Email: "",
    EndDate: "",
    Status: false,
    packageName: "",
    LicensedPondCount: 1,
    isSubscriptionActive: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [billingAction, setBillingAction] = useState(null);
  const [stripeBilling, setStripeBilling] = useState(null);
  const [closingAccount, setClosingAccount] = useState(false);

  useEffect(() => {
    if (isIndependentScientistUser(userDetail)) {
      setIsLoading(false);
      setLoadError(null);
      return;
    }

    // Unscoped company manager: load company entity, not Fisher.
    if (isCompanyManager && !viewingScopedClient) {
      let cancelled = false;
      (async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
          const { data } = await axiosInstance.get("api/company/me");
          if (cancelled) return;
          setCompanyProfile(data);
          const c = data?.company || {};
          setCompanyForm({
            Name: c.Name || "",
            Email: c.Email || "",
            Phone: c.Phone || "",
            Address: c.Address || "",
            City: c.City || "",
            State: c.State || "",
            PostalCode: c.PostalCode || "",
            Country: c.Country || "",
            EIN: c.EIN || "",
          });
          if (data?.User) {
            mergeUserDetails({
              companyName: data.User.companyName || data.User.CompanyName,
              CompanyName: data.User.CompanyName || data.User.companyName,
              companyEmail: data.User.companyEmail,
              CompanyEmail: data.User.CompanyEmail,
              Phone: data.User.Phone,
              Address: data.User.Address,
              City: data.User.City,
              State: data.User.State,
              PostalCode: data.User.PostalCode,
              EIN: data.User.EIN,
              Country: data.User.Country,
              companyStatus: data.User.companyStatus,
              sandboxFisherId: data.User.sandboxFisherId,
              sandboxFisherName: data.User.sandboxFisherName,
            });
          }
        } catch (error) {
          if (!cancelled) {
            setLoadError(
              error.response?.data?.message || "Could not load company profile."
            );
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (fisherID == null || fisherID === "") {
      setIsLoading(false);
      setLoadError("Missing organization context.");
      return;
    }
    let cancelled = false;
    const fetchFisher = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await axiosInstance.get(`/api/fisher/GetFisher?id=${fisherID}`);
        const fetchedFisher = response.data;
        fetchedFisher.EndDate = fetchedFisher.EndDate
          ? new Date(fetchedFisher.EndDate).toISOString().split("T")[0]
          : "";
        if (!cancelled) setFisher(fetchedFisher);
      } catch (error) {
        console.error("Error fetching fisher:", error);
        if (!cancelled) {
          setLoadError(
            error.response?.data?.message || "Could not load organization details."
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchFisher();
    return () => {
      cancelled = true;
    };
  }, [fisherID, isCompanyManager, viewingScopedClient]);

  useEffect(() => {
    if (isCompanyManager && !viewingScopedClient) return;
    const sid = userDetail.id;
    if (sid == null || sid === "") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get("api/User/GetStaff", { params: { id: sid } });
        if (cancelled || !data) return;
        const owner = truthyFlag(data.IsFisherOwner);
        mergeUserDetails({
          IsAdmin: !!data.IsAdmin,
          IsFisherOwner: owner,
        });
        setStaffFlags({ IsAdmin: !!data.IsAdmin, IsFisherOwner: owner });
      } catch {
        /* keep session-only flags */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userDetail.id, isCompanyManager, viewingScopedClient]);

  const userForRole = { ...userDetail, ...(staffFlags || {}) };
  const roleInfo = describeUserRole(userForRole);
  const independent = isIndependentScientistUser(userForRole);
  const showCompanyCard = isCompanyManager && !viewingScopedClient;

  useEffect(() => {
    if (showCompanyCard || independent || fisherID == null || fisherID === "") return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get("api/fisher/SubscriptionInfo");
        if (!cancelled) setStripeBilling(data);
      } catch {
        if (!cancelled) setStripeBilling(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fisherID, independent, showCompanyCard]);

  const handleCancelRenewal = async () => {
    setBillingAction("cancel");
    try {
      const { data } = await axiosInstance.post("api/fisher/CancelStripeRenewal");
      showSuccessToast(data.message || "Auto-renewal turned off");
      if (data.trialCancelled) {
        setStripeBilling(null);
        window.location.href = "/packages";
        return;
      }
      setStripeBilling((prev) => ({
        ...prev,
        stripeCancelAtPeriodEnd: true,
        recurringBilling: { ...prev?.recurringBilling, cancelAtPeriodEnd: true },
      }));
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Could not cancel auto-renewal");
    } finally {
      setBillingAction(null);
    }
  };

  const handleResumeRenewal = async () => {
    setBillingAction("resume");
    try {
      const { data } = await axiosInstance.post("api/fisher/ResumeStripeRenewal");
      showSuccessToast(data.message || "Auto-renewal resumed");
      setStripeBilling((prev) => ({
        ...prev,
        stripeCancelAtPeriodEnd: false,
        recurringBilling: { ...prev?.recurringBilling, cancelAtPeriodEnd: false },
      }));
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Could not resume auto-renewal");
    } finally {
      setBillingAction(null);
    }
  };

  const startEditCompany = () => {
    const c = companyProfile?.company || {};
    setCompanyForm({
      Name: c.Name || userDetail.companyName || userDetail.CompanyName || "",
      Email: c.Email || userDetail.companyEmail || userDetail.CompanyEmail || "",
      Phone: c.Phone || userDetail.Phone || "",
      Address: c.Address || userDetail.Address || "",
      City: c.City || userDetail.City || "",
      State: c.State || userDetail.State || "",
      PostalCode: c.PostalCode || userDetail.PostalCode || "",
      Country: c.Country || userDetail.Country || "",
      EIN: c.EIN || userDetail.EIN || "",
    });
    setEditingCompany(true);
  };

  const handleCompanyFormChange = (e) => {
    const { name, value } = e.target;
    setCompanyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    const name = String(companyForm.Name || "").trim();
    const email = String(companyForm.Email || "").trim();
    if (!name) {
      showErrorToast("Organization name is required");
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showErrorToast("A valid organization email is required");
      return;
    }
    setSavingCompany(true);
    try {
      const { data } = await axiosInstance.post("api/company/me/update", {
        Name: name,
        Email: email,
        Phone: companyForm.Phone,
        Address: companyForm.Address,
        City: companyForm.City,
        State: companyForm.State,
        PostalCode: companyForm.PostalCode,
        Country: companyForm.Country,
        EIN: companyForm.EIN,
      });
      setCompanyProfile(data);
      const c = data?.company || {};
      setCompanyForm({
        Name: c.Name || "",
        Email: c.Email || "",
        Phone: c.Phone || "",
        Address: c.Address || "",
        City: c.City || "",
        State: c.State || "",
        PostalCode: c.PostalCode || "",
        Country: c.Country || "",
        EIN: c.EIN || "",
      });
      if (data?.User) {
        mergeUserDetails({
          companyName: data.User.companyName || data.User.CompanyName,
          CompanyName: data.User.CompanyName || data.User.companyName,
          companyEmail: data.User.companyEmail,
          CompanyEmail: data.User.CompanyEmail,
          Phone: data.User.Phone,
          Address: data.User.Address,
          City: data.User.City,
          State: data.User.State,
          PostalCode: data.User.PostalCode,
          EIN: data.User.EIN,
          Country: data.User.Country,
          companyStatus: data.User.companyStatus,
        });
      }
      setEditingCompany(false);
      showSuccessToast(data.message || "Company profile updated");
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Could not update company profile");
    } finally {
      setSavingCompany(false);
    }
  };

  const canCloseOwnFisherAccount =
    !independent &&
    !isCompanyManager &&
    truthyFlag(staffFlags?.IsFisherOwner || userDetail.IsFisherOwner);

  const canCloseOwnCompanyAccount =
    showCompanyCard &&
    truthyFlag(companyProfile?.manager?.IsCompanyOwner ?? userDetail.IsCompanyOwner);

  const handleCloseMyAccount = async () => {
    if (closingAccount) return;
    const isCompanyClose = canCloseOwnCompanyAccount;
    const emailHint = String(userDetail.Email || "").trim();

    const result = await Swal.fire({
      title: isCompanyClose ? "Delete company account?" : "Delete my account?",
      html: isCompanyClose
        ? `
        <p class="text-start small mb-2">Delete this company account. Manager logins and company identity are removed. Managed pond owners are released.</p>
        <p class="text-start small text-muted mb-0">Type <strong>DELETE</strong> to confirm.</p>
      `
        : `
        <p class="text-start small mb-2">Delete your account. Your personal information and all other data will be removed.</p>
        <p class="text-start small text-muted mb-0">Type <strong>DELETE</strong> to confirm.</p>
      `,
      input: "text",
      inputPlaceholder: "DELETE",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: isCompanyClose ? "Delete company" : "Delete account",
      cancelButtonText: "Cancel",
      focusCancel: true,
      inputValidator: (value) => {
        if (String(value || "").trim() !== "DELETE") {
          return "Type DELETE to confirm";
        }
        return null;
      },
    });
    if (!result.isConfirmed) return;

    setClosingAccount(true);
    try {
      const endpoint = isCompanyClose
        ? "api/company/CloseMyAccount"
        : "api/fisher/CloseMyAccount";
      const res = await axiosInstance.post(endpoint, { confirm: true });
      showSuccessToast(res.data?.message || "Account closed");
      clearAuthData();
      window.location.href = "/user/login";
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          `Failed to delete account${emailHint ? ` (${emailHint})` : ""}.`
      );
      setClosingAccount(false);
    }
  };

  const initials = useMemo(
    () => initialsFromName(userDetail.FullName),
    [userDetail.FullName]
  );

  const endDateLabel = formatDisplayDateLong(fisher.EndDate);
  const isStripeTrialing = stripeBilling?.recurringBilling?.status === "trialing";
  const stripeTrialEndLabel = formatDisplayDateLong(stripeBilling?.recurringBilling?.trialEnd);

  const packageBilling = useMemo(() => {
    if (fisher.isTrialPeriod) return null;

    const stripeLabel = formatStripeRecurringLabel(stripeBilling?.recurringBilling);
    if (stripeLabel) {
      const duration = fisher.package?.Duration ?? 12;
      return {
        label: duration === 12 || stripeBilling?.recurringBilling?.interval === "year"
          ? "Annual subscription"
          : "Subscription price",
        value: stripeLabel,
      };
    }

    const pkg = fisher.package;
    if (!pkg) return null;
    const amount = computePeriodAmount(pkg, fisher.LicensedPondCount);
    if (amount == null) return null;
    const duration = pkg.Duration ?? 12;
    return {
      label: duration === 12 ? "Annual subscription" : "Subscription price",
      value: formatBillingPeriodLabel(amount, duration),
    };
  }, [fisher, stripeBilling]);

  const InfoRow = ({ label, children }) => (
    <div className="donezo-profile-row">
      <div className="donezo-profile-row__label">{label}</div>
      <div className="donezo-profile-row__value">{children}</div>
    </div>
  );

  const StatusDot = ({ tone, children }) => (
    <span className={`donezo-status-dot donezo-status-dot--${tone}`}>
      <span className="donezo-status-dot__mark" aria-hidden />
      {children}
    </span>
  );

  if (isLoading) return <Preloader />;

  const teamHref = isCompanyManagerUser()
    ? getScopedAccount()?.id
      ? "/staff"
      : "/company/accounts"
    : "/staff";

  return (
    <div className="donezo-page donezo-list-page donezo-orders-page donezo-upload-page donezo-profile-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <div className="donezo-orders-toolbar donezo-upload-toolbar">
          <div className="donezo-upload-toolbar__intro">
            <h1 className="donezo-orders-toolbar__title">Profile</h1>
            <p className="donezo-upload-toolbar__subtitle">
              {independent
                ? "Your sign-in identity and scientist account settings."
                : "Your sign-in identity and organization subscription summary."}
            </p>
          </div>
          <div className="donezo-orders-toolbar__actions">
            {!independent && (
              <Link to={teamHref} className="donezo-orders-toolbar__ghost">
                <i className="fa fa-users" aria-hidden />
                <span>Team</span>
              </Link>
            )}
            {independent && (
              <Link to="/scientistInvites" className="donezo-orders-toolbar__ghost">
                <i className="fa fa-envelope" aria-hidden />
                <span>Invitations</span>
              </Link>
            )}
          </div>
        </div>

        {loadError && (
          <div className="donezo-upload-notice donezo-upload-notice--warn mb-3">{loadError}</div>
        )}

        <div className="donezo-orders-panel donezo-upload-section mb-3">
          <div className="donezo-upload-section__header">
            <i className="fa fa-user" aria-hidden />
            <h2 className="donezo-upload-section__title">Account</h2>
          </div>
          <div className="donezo-upload-section__body">
            <div className="donezo-profile-hero">
              <span className="donezo-orders-pond__avatar donezo-profile-hero__avatar" aria-hidden>
                {initials}
              </span>
              <div className="donezo-profile-hero__meta">
                <h2 className="donezo-profile-hero__name">{userDetail.FullName || "Member"}</h2>
                <p className="donezo-team-email mb-2" title={userDetail.Email || undefined}>
                  {userDetail.Email || "—"}
                </p>
                <div className="d-flex flex-wrap gap-3">
                  <StatusDot tone={userDetail.Status ? "natural" : "other"}>
                    {userDetail.Status ? "Active" : "Inactive"}
                  </StatusDot>
                  <StatusDot tone="stocked">{roleInfo.title}</StatusDot>
                </div>
              </div>
            </div>

            <div className="donezo-profile-rows mt-3">
              <InfoRow label="Full name">{userDetail.FullName || "—"}</InfoRow>
              <InfoRow label="Email">{userDetail.Email || "—"}</InfoRow>
              <InfoRow label="Your role">
                <span className="fw-semibold">{roleInfo.title}</span>
                {roleInfo.subtitle ? (
                  <span className="d-block donezo-page-fine-print fw-normal mt-1">
                    {roleInfo.subtitle}
                  </span>
                ) : null}
              </InfoRow>
              <InfoRow label="Sign-in status">
                {userDetail.Status ? (
                  <span>You can sign in to Pondstat.</span>
                ) : (
                  <span>This account is disabled. Contact your owner.</span>
                )}
              </InfoRow>
            </div>
          </div>
        </div>

        {!independent && showCompanyCard && (
          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header donezo-upload-section__header--split">
              <div className="d-flex align-items-center gap-2">
                <i className="fa fa-building" aria-hidden />
                <h2 className="donezo-upload-section__title">Company details</h2>
              </div>
              {!editingCompany ? (
                <button
                  type="button"
                  className="donezo-orders-toolbar__ghost"
                  onClick={startEditCompany}
                >
                  <i className="fa fa-pen" aria-hidden />
                  <span>Edit</span>
                </button>
              ) : null}
            </div>
            <div className="donezo-upload-section__body">
              <p className="donezo-upload-section__hint">
                Organization name and email for your Pond Management Company. Separate from your
                login email. Client subscriptions are managed under Company Accounts.
              </p>

              {editingCompany ? (
                <form onSubmit={handleSaveCompany}>
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-medium" htmlFor="company-org-name">
                        Organization name *
                      </label>
                      <input
                        id="company-org-name"
                        name="Name"
                        className="form-control"
                        value={companyForm.Name}
                        onChange={handleCompanyFormChange}
                        required
                        autoComplete="organization"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-medium" htmlFor="company-org-email">
                        Organization email *
                      </label>
                      <input
                        id="company-org-email"
                        name="Email"
                        type="email"
                        className="form-control"
                        value={companyForm.Email}
                        onChange={handleCompanyFormChange}
                        required
                        autoComplete="organization"
                      />
                      <div className="form-text">
                        Public / billing contact. Does not change your login email.
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-medium" htmlFor="company-phone">
                        Phone
                      </label>
                      <input
                        id="company-phone"
                        name="Phone"
                        type="tel"
                        className="form-control"
                        value={companyForm.Phone}
                        onChange={handleCompanyFormChange}
                        autoComplete="tel"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-medium" htmlFor="company-ein">
                        EIN
                      </label>
                      <input
                        id="company-ein"
                        name="EIN"
                        className="form-control"
                        value={companyForm.EIN}
                        onChange={handleCompanyFormChange}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-medium" htmlFor="company-address">
                        Address
                      </label>
                      <input
                        id="company-address"
                        name="Address"
                        className="form-control"
                        value={companyForm.Address}
                        onChange={handleCompanyFormChange}
                        autoComplete="street-address"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-medium" htmlFor="company-city">
                        City
                      </label>
                      <input
                        id="company-city"
                        name="City"
                        className="form-control"
                        value={companyForm.City}
                        onChange={handleCompanyFormChange}
                        autoComplete="address-level2"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-medium" htmlFor="company-state">
                        State / region
                      </label>
                      <input
                        id="company-state"
                        name="State"
                        className="form-control"
                        value={companyForm.State}
                        onChange={handleCompanyFormChange}
                        autoComplete="address-level1"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-medium" htmlFor="company-postal">
                        Postal / ZIP
                      </label>
                      <input
                        id="company-postal"
                        name="PostalCode"
                        className="form-control"
                        value={companyForm.PostalCode}
                        onChange={handleCompanyFormChange}
                        autoComplete="postal-code"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-medium" htmlFor="company-country">
                        Country
                      </label>
                      <input
                        id="company-country"
                        name="Country"
                        className="form-control"
                        value={companyForm.Country}
                        onChange={handleCompanyFormChange}
                        autoComplete="country"
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="submit"
                      className="donezo-orders-toolbar__primary"
                      disabled={savingCompany}
                    >
                      {savingCompany ? "Saving…" : "Save company profile"}
                    </button>
                    <button
                      type="button"
                      className="donezo-orders-toolbar__ghost"
                      disabled={savingCompany}
                      onClick={() => setEditingCompany(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="donezo-profile-rows">
                  <InfoRow label="Organization name">
                    {companyProfile?.company?.Name ||
                      userDetail.companyName ||
                      userDetail.CompanyName ||
                      "—"}
                  </InfoRow>
                  <InfoRow label="Organization email">
                    {companyProfile?.company?.Email ||
                      userDetail.companyEmail ||
                      userDetail.CompanyEmail ||
                      "—"}
                  </InfoRow>
                  <InfoRow label="Phone">
                    {companyProfile?.company?.Phone || userDetail.Phone || "—"}
                  </InfoRow>
                  <InfoRow label="Address">
                    {companyProfile?.company?.Address || userDetail.Address || "—"}
                  </InfoRow>
                  <InfoRow label="City">
                    {companyProfile?.company?.City || userDetail.City || "—"}
                  </InfoRow>
                  <InfoRow label="State / region">
                    {companyProfile?.company?.State || userDetail.State || "—"}
                  </InfoRow>
                  <InfoRow label="Postal / ZIP">
                    {companyProfile?.company?.PostalCode || userDetail.PostalCode || "—"}
                  </InfoRow>
                  <InfoRow label="Country">
                    {companyProfile?.company?.Country || userDetail.Country || "—"}
                  </InfoRow>
                  <InfoRow label="EIN">
                    {companyProfile?.company?.EIN || userDetail.EIN || (
                      <span className="donezo-page-fine-print">Not provided</span>
                    )}
                  </InfoRow>
                  <InfoRow label="Company status">
                    <StatusDot
                      tone={
                        companyProfile?.company?.Status ?? userDetail.companyStatus
                          ? "natural"
                          : "other"
                      }
                    >
                      {companyProfile?.company?.Status ?? userDetail.companyStatus
                        ? "Active"
                        : "Inactive"}
                    </StatusDot>
                  </InfoRow>
                  {companyProfile?.sandboxFisher ? (
                    <InfoRow label="Demo account">
                      {companyProfile.sandboxFisher.FullName}
                      <span className="d-block donezo-page-fine-print fw-normal mt-1">
                        Free test capacity (
                        {companyProfile.sandboxFisher.LicensedPondCount ?? 1} licensed pond) — open
                        it from Company Accounts to create ponds.
                      </span>
                    </InfoRow>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}

        {!independent && !showCompanyCard && (
          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i className="fa fa-briefcase" aria-hidden />
              <h2 className="donezo-upload-section__title">Organization &amp; subscription</h2>
            </div>
            <div className="donezo-upload-section__body">
              <p className="donezo-upload-section__hint">
                {viewingScopedClient
                  ? `Viewing client account${
                      getScopedAccount()?.name ? `: ${getScopedAccount().name}` : ""
                    }.`
                  : "Information for your pond organization linked to this login."}
              </p>
              <div className="donezo-profile-rows">
                <InfoRow label="Organization name">{fisher.FullName || "—"}</InfoRow>
                <InfoRow label="Organization email">{fisher.Email || "—"}</InfoRow>
                <InfoRow label="Package">
                  {fisher.packageName ? (
                    <span className="fw-semibold">
                      {fisher.packageName}
                      {fisher.isTrialPeriod ? (
                        <span className="d-block donezo-page-fine-print fw-normal mt-1">
                          Free trial — choose a package before your trial ends.
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="donezo-page-fine-print">No package assigned</span>
                  )}
                </InfoRow>
                <InfoRow label="Licensed ponds">
                  {fisher.LicensedPondCount != null ? fisher.LicensedPondCount : "—"}
                </InfoRow>
                {packageBilling ? (
                  <InfoRow label={packageBilling.label}>{packageBilling.value}</InfoRow>
                ) : null}
                {isStripeTrialing && stripeTrialEndLabel && (
                  <InfoRow label="Trial ends">
                    {stripeTrialEndLabel}
                    <span className="d-block donezo-page-fine-print fw-normal mt-1">
                      Your card will not be charged if you cancel before this date.
                    </span>
                  </InfoRow>
                )}
                <InfoRow label="Subscription end date">
                  {endDateLabel || (
                    <span className="donezo-page-fine-print">No end date on file</span>
                  )}
                </InfoRow>
                <InfoRow label="Subscription status">
                  {isStripeTrialing ? (
                    <StatusDot tone="stocked">Free trial</StatusDot>
                  ) : fisher.hasSoftwareAccess === true ||
                    fisher.isSubscriptionActive === true ? (
                    <StatusDot tone="natural">Active</StatusDot>
                  ) : fisher.hasSoftwareAccess === false ||
                    fisher.isSubscriptionActive === false ? (
                    <StatusDot tone="river">Expired</StatusDot>
                  ) : (
                    "—"
                  )}
                </InfoRow>
                <InfoRow label="Organization status">
                  {fisher.Status !== undefined && fisher.Status !== null ? (
                    fisher.Status ? (
                      <StatusDot tone="natural">Active</StatusDot>
                    ) : (
                      <StatusDot tone="other">Inactive</StatusDot>
                    )
                  ) : (
                    "—"
                  )}
                </InfoRow>
                {stripeBilling?.country && (
                  <InfoRow label="Billing country">{stripeBilling.country}</InfoRow>
                )}
                {stripeBilling?.stripeSubscriptionId && (
                  <InfoRow label="Auto-renewal">
                    {stripeBilling.stripeCancelAtPeriodEnd ? (
                      <StatusDot tone="river">Off — access until end date</StatusDot>
                    ) : (
                      <StatusDot tone="natural">On</StatusDot>
                    )}
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {stripeBilling.stripeCancelAtPeriodEnd ? (
                        <button
                          type="button"
                          className="donezo-orders-toolbar__ghost"
                          disabled={billingAction === "resume"}
                          onClick={handleResumeRenewal}
                        >
                          Resume auto-renewal
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="donezo-orders-toolbar__ghost"
                          disabled={billingAction === "cancel"}
                          onClick={handleCancelRenewal}
                        >
                          {isStripeTrialing ? "Cancel trial" : "Turn off auto-renewal"}
                        </button>
                      )}
                    </div>
                    <p className="donezo-page-fine-print mb-0 mt-2">
                      {isStripeTrialing
                        ? "Cancelling during your trial stops future billing and removes package access. Your card will not be charged."
                        : "Turning off auto-renewal stops future Stripe charges. You keep access until your subscription end date."}
                    </p>
                  </InfoRow>
                )}
              </div>
            </div>
          </div>
        )}

        {(canCloseOwnFisherAccount || canCloseOwnCompanyAccount) && (
          <div className="donezo-orders-panel donezo-upload-section donezo-profile-danger">
            <div className="donezo-upload-section__header">
              <i className="fa fa-exclamation-triangle" aria-hidden />
              <h2 className="donezo-upload-section__title">Danger zone</h2>
            </div>
            <div className="donezo-upload-section__body">
              <p className="donezo-upload-section__hint">
                {canCloseOwnCompanyAccount
                  ? "Delete this company account. Manager logins and company identity are removed. Managed pond owners are released."
                  : "Delete your account. Your personal information and all other data will be removed."}
              </p>
              <button
                type="button"
                className="donezo-orders-toolbar__ghost donezo-profile-danger__btn"
                disabled={closingAccount}
                onClick={handleCloseMyAccount}
              >
                {closingAccount
                  ? "Deleting…"
                  : canCloseOwnCompanyAccount
                    ? "Delete company account"
                    : "Delete my account"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
